// Editors for the People table's list columns (parents, partners, siblings, children, friends,
// groups). They float under their cell in a portal, since a list doesn't fit inside a cell.

import { Lock, Pencil, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { bulkEdit } from '../../model/bulk';
import { displayName, newId, shortName } from '../../model/people';
import { addRelative, relativesOf, removeRelative, type KinColumn, type Relative } from '../../model/relatives';
import { addNewPerson, upsertGroup } from '../../model/tree';
import type { Id, Person, Relationship, Tree } from '../../model/types';
import { GROUP_COLORS, PARENT_TYPE_LABELS, PARTNER_STATUS_LABELS, PARTNER_TYPE_LABELS, SIBLING_TYPE_LABELS, SOCIAL_TYPE_LABELS } from '../labels';
import { commit, getState, openDialog, toast, undo, useApp } from '../store';
import { Avatar } from './common';
import { PersonPicker } from './PersonPicker';

/** Leave the editor; `move` goes on to the next (1) or previous (-1) cell, as Tab does in the table. */
export type LinkDone = (move?: 1 | -1) => void;

const NOUN: Record<KinColumn, string> = { parents: 'parent', partners: 'partner', siblings: 'sibling', children: 'child', friends: 'friend' };
const TITLE: Record<KinColumn, string> = { parents: 'Parents', partners: 'Partners', siblings: 'Siblings', children: 'Children', friends: 'Friends' };

/** How a link differs from the plain kind, e.g. "Adoptive" or "Married · divorced". */
function linkLabel(r: Relationship): string {
  if (r.label) return r.label;
  switch (r.kind) {
    case 'parent': return r.type === 'biological' ? '' : PARENT_TYPE_LABELS[r.type];
    case 'partner': return [PARTNER_TYPE_LABELS[r.type], r.status !== 'current' && PARTNER_STATUS_LABELS[r.status].toLowerCase()].filter(Boolean).join(' · ');
    case 'sibling': return r.type === 'unknown' ? '' : SIBLING_TYPE_LABELS[r.type];
    case 'social': return r.type === 'friend' ? '' : SOCIAL_TYPE_LABELS[r.type];
  }
}

/** A new person from a typed name: the last word is the last name. */
function personFromText(t: Tree, text: string): { tree: Tree; person: Person } {
  const words = text.trim().split(/\s+/);
  const surname = words.length > 1 ? words.pop()! : '';
  return addNewPerson(t, { names: [{ given: words.join(' '), surname, type: 'name' }] });
}

export function KinEditor({ person: p, col, onDone }: { person: Person; col: KinColumn; onDone: LinkDone }) {
  const tree = useApp((s) => s.tree)!;
  const list = relativesOf(tree, p.id, col);
  const name = shortName(p);
  const noun = NOUN[col];

  const add = (t: Tree, otherId: Id) => {
    const r = addRelative(t, p.id, col, otherId);
    const them = displayName(t.people[otherId]);
    if ('tree' in r) {
      commit(r.tree);
      return;
    }
    if (r.problem === 'exists') toast(`${them} is already ${name}’s ${noun}.`, 'error');
    else if (r.problem === 'cycle') toast(`${them} can’t be ${name}’s ${noun}: that would make someone their own ancestor.`, 'error');
    else if (r.problem === 'connected') toast(`${them} and ${name} are already connected another way. Change that connection instead.`, 'error');
  };
  const remove = (rel: Relative) => {
    commit(removeRelative(getState().tree!, p.id, col, rel.id));
    toast(`Removed ${displayName(tree.people[rel.id])} as ${name}’s ${noun}.`, 'info', { label: 'Undo', run: undo });
  };

  return (
    <CellPopover label={`${TITLE[col]} of ${displayName(p)}`} onDone={onDone}>
      <div className="link-list">
        {list.map((rel) => {
          const them = tree.people[rel.id];
          // Siblings are described as the tree works them out (half, step…); other links by their own type.
          const what = rel.siblingType ? (rel.rels[0]?.label ?? (rel.siblingType === 'unknown' ? '' : SIBLING_TYPE_LABELS[rel.siblingType])) : rel.rels.map(linkLabel).filter(Boolean).join(', ');
          return (
            <div key={rel.id} className="link-row">
              <Avatar person={them} size="sm" />
              <span className="link-name">
                {displayName(them)}
                {what && <span className="faint small-text"> {what}</span>}
              </span>
              {rel.rels[0] && (
                <button type="button" className="btn icon small ghost" onClick={() => openDialog({ type: 'edit-relationship', relId: rel.rels[0].id })} aria-label={`Edit how ${displayName(them)} is linked`} title="Edit relationship">
                  <Pencil size={13} />
                </button>
              )}
              {rel.throughParents ? (
                <span
                  className="link-locked faint"
                  role="img"
                  aria-label="Siblings through shared parents"
                  title={`Siblings through shared parents (${rel.throughParents.map((id) => shortName(tree.people[id])).join(', ')}). Change those parent links to change this.`}
                >
                  <Lock size={13} />
                </span>
              ) : (
                <button type="button" className="btn icon small ghost" onClick={() => remove(rel)} aria-label={`Remove ${displayName(them)} as ${name}’s ${noun}`} title="Remove">
                  <X size={14} />
                </button>
              )}
            </div>
          );
        })}
        {!list.length && <div className="faint small-text link-empty">No {TITLE[col].toLowerCase()} yet.</div>}
      </div>
      <PersonPicker
        tree={tree}
        value={null}
        onChange={(id) => id && add(getState().tree!, id)}
        exclude={[p.id, ...list.map((r) => r.id)]}
        placeholder={`Add a ${noun}…`}
        ariaLabel={`Add a ${noun}`}
        onCreate={(text) => {
          const r = personFromText(getState().tree!, text);
          add(r.tree, r.person.id);
        }}
      />
    </CellPopover>
  );
}

export function GroupsEditor({ person: p, onDone }: { person: Person; onDone: LinkDone }) {
  const tree = useApp((s) => s.tree)!;
  const groups = Object.values(tree.groups).sort((a, b) => a.name.localeCompare(b.name));
  const mine = groups.filter((g) => g.members.some((m) => m.personId === p.id));
  const others = groups.filter((g) => !mine.includes(g));
  const [naming, setNaming] = useState<string | null>(null);
  const change = (next: Tree) => commit(next);
  // Back to the dropdown after naming a new group, so the keyboard isn't left on the page.
  const selectRef = useRef<HTMLSelectElement>(null);
  const wasNaming = useRef(false);
  useEffect(() => {
    if (naming === null && wasNaming.current) selectRef.current?.focus();
    wasNaming.current = naming !== null;
  }, [naming]);

  const create = () => {
    const name = naming?.trim();
    setNaming(null);
    if (!name) return;
    const t = getState().tree!;
    change(upsertGroup(t, { id: newId('g'), name, kind: 'friend-group', description: '', color: GROUP_COLORS[Object.keys(t.groups).length % GROUP_COLORS.length], members: [{ personId: p.id }] }));
  };

  return (
    <CellPopover label={`Groups of ${displayName(p)}`} onDone={onDone}>
      <div className="link-list">
        {mine.map((g) => {
          const role = g.members.find((m) => m.personId === p.id)?.role;
          return (
            <div key={g.id} className="link-row">
              <span className="group-dot" style={{ background: g.color }} aria-hidden />
              <span className="link-name">
                {g.name}
                {role && <span className="faint small-text"> {role}</span>}
              </span>
              <button
                type="button"
                className="btn icon small ghost"
                onClick={() => {
                  change(bulkEdit(getState().tree!, [p.id], { removeFromGroup: g.id }).tree);
                  toast(`Removed ${shortName(p)} from ${g.name}.`, 'info', { label: 'Undo', run: undo });
                }}
                aria-label={`Remove from ${g.name}`}
                title="Remove from group"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
        {!mine.length && <div className="faint small-text link-empty">Not in any groups yet.</div>}
      </div>
      {naming !== null ? (
        <input
          className="input"
          autoFocus
          aria-label="New group name"
          placeholder="New group name"
          value={naming}
          onChange={(e) => setNaming(e.target.value)}
          onBlur={create}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              create();
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              setNaming(null);
            }
          }}
        />
      ) : (
        <select
          ref={selectRef}
          className="select"
          aria-label="Add to a group"
          value=""
          onChange={(e) => {
            const v = e.target.value;
            if (v === '+new') return setNaming('');
            if (v) change(bulkEdit(getState().tree!, [p.id], { addToGroup: { groupId: v } }).tree);
          }}
        >
          <option value="">Add to a group…</option>
          {others.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
          <option value="+new">New group…</option>
        </select>
      )}
    </CellPopover>
  );
}

/**
 * A panel under the cell being edited. It closes on Escape or a press outside it, and Tab past
 * its first or last control moves to the neighboring cell, like the table's other editors.
 */
function CellPopover({ label, onDone, children }: { label: string; onDone: LinkDone; children: ReactNode }) {
  const anchor = useRef<HTMLSpanElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const done = useRef(onDone);
  done.current = onDone;

  useLayoutEffect(() => {
    const place = () => {
      const cell = anchor.current?.closest('td');
      const r = cell?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(Math.max(r.width, 300), window.innerWidth - 16);
      const h = ref.current?.offsetHeight ?? 0;
      const below = r.bottom + 4 + h <= window.innerHeight - 8 || r.top < h + 12;
      setPos({ left: Math.max(8, Math.min(r.left, window.innerWidth - 8 - width)), top: below ? r.bottom + 4 : r.top - 4 - h, width });
    };
    place();
    window.addEventListener('resize', place);
    document.addEventListener('scroll', place, true);
    const ro = new ResizeObserver(place);
    if (ref.current) ro.observe(ref.current);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('scroll', place, true);
      ro.disconnect();
    };
  }, []);

  // Focus once placed: it's hidden while measuring, and hidden fields can't take focus.
  const placed = !!pos;
  useEffect(() => {
    if (placed && !ref.current?.contains(document.activeElement)) ref.current?.querySelector<HTMLElement>('input, select')?.focus();
  }, [placed]);

  useEffect(() => {
    const down = (e: PointerEvent) => {
      const t = e.target as Element;
      // Its own lists and menus, and dialogs opened from it, are part of it.
      if (ref.current?.contains(t) || t.closest?.('.picker-list, .menu, .overlay')) return;
      done.current();
    };
    document.addEventListener('pointerdown', down, true);
    return () => document.removeEventListener('pointerdown', down, true);
  }, []);

  return (
    <>
      <span ref={anchor} hidden />
      {createPortal(
        <div
          ref={ref}
          className="cell-popover"
          role="dialog"
          aria-label={label}
          style={pos ? { left: pos.left, top: pos.top, width: pos.width } : { visibility: 'hidden' }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              done.current();
            } else if (e.key === 'Tab') {
              const items = [...(ref.current?.querySelectorAll<HTMLElement>('button, input, select') ?? [])].filter((x) => !x.hasAttribute('disabled'));
              const last = e.shiftKey ? items[0] : items.at(-1);
              if (e.target === last) {
                e.preventDefault();
                done.current(e.shiftKey ? -1 : 1);
              }
            }
          }}
        >
          <div className="cell-popover-title">{label}</div>
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}
